import { useParams, Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ScrollToTop from "@/components/base/ScrollToTop";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const categoryArticles = useMemo(
    () => (category ? articles.filter((a) => a.categorySlug === slug) : []),
    [category, slug]
  );

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - Po文完全指南`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", category.description);
      const metaKw = document.querySelector('meta[name="keywords"]');
      if (metaKw) metaKw.setAttribute("content", `po文,${category.name},po文技巧,po文指南,内容创作`);
    }
  }, [category]);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 mb-4 block"></i>
            <h1 className="text-xl font-bold text-foreground-900 mb-2">分类不存在</h1>
            <p className="text-foreground-600 text-sm mb-6">你访问的分类页面未找到</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: category.name },
  ];

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - Po文完全指南`,
    description: category.description,
    url: `https://powen.guide/category/${slug}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: categoryArticles.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://powen.guide/article/${a.slug}`,
        name: a.title,
      })),
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://powen.guide" },
      { "@type": "ListItem", position: 2, name: category.name },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-24 pb-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <div className="mt-6 mb-10">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900"
                  style={{ fontFamily: "var(--font-heading)" }}>
                  {category.name}
                </h1>
                <p className="text-foreground-600 text-sm mt-1">{category.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-10">
            <span className="text-sm text-foreground-500 whitespace-nowrap">浏览其他分类：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} {...article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-600">此分类暂无文章，敬请期待</p>
            </div>
          )}
        </div>
      </main>
      <Footer />

      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </>
  );
}